/**
 * WizardStepper Component
 * Shows progress through wizard steps
 */
import React from 'react';
import { WIZARD_STEPS } from './hooks/useWizardState';

const steps = [
  { 
    id: WIZARD_STEPS.UPLOAD, 
    label: 'Upload', 
    icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>, 
    description: 'Dateien hochladen' 
  },
  { 
    id: WIZARD_STEPS.PREVIEW, 
    label: 'Vorschau', 
    icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>, 
    description: 'Seiten prüfen' 
  },
  { 
    id: WIZARD_STEPS.CHUNKING, 
    label: 'Strategie', 
    icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>, 
    description: 'Verarbeitung wählen' 
  },
  { 
    id: WIZARD_STEPS.GENERATE, 
    label: 'Generieren', 
    icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>, 
    description: 'Fragen erstellen' 
  }
];

function WizardStepper({ currentStep, onStepClick, canNavigateTo = () => true }) {
  return (
    <div className="mb-8">
      {/* Desktop Stepper */}
      <div className="hidden sm:flex items-center justify-between">
        {steps.map((step, index) => {
          const isActive = currentStep === step.id;
          const isCompleted = currentStep > step.id;
          const canClick = canNavigateTo(step.id);
          
          return (
            <React.Fragment key={step.id}>
              {/* Step Circle */}
              <button
                onClick={() => canClick && onStepClick(step.id)}
                disabled={!canClick}
                className={`
                  flex flex-col items-center group relative
                  ${canClick ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}
                `}
              >
                <div className={`
                  w-12 h-12 rounded-full flex items-center justify-center text-lg
                  transition-all duration-200
                  ${isActive 
                    ? 'bg-primary-500 text-white ring-4 ring-primary-100 dark:ring-primary-900' 
                    : isCompleted
                      ? 'bg-success-500 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }
                  ${canClick && !isActive ? 'group-hover:ring-2 group-hover:ring-primary-200 dark:group-hover:ring-primary-800' : ''}
                `}>
                  {isCompleted ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : step.icon}
                </div>
                <span className={`
                  mt-2 text-sm font-medium
                  ${isActive 
                    ? 'text-primary-600 dark:text-primary-400' 
                    : 'text-gray-500 dark:text-gray-400'
                  }
                `}>
                  {step.label}
                </span>
                <span className="text-xs text-gray-400 dark:text-gray-500 hidden md:block">
                  {step.description}
                </span>
              </button>
              
              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div className={`
                  flex-1 h-1 mx-4 rounded-full theme-transition
                  ${currentStep > step.id
                    ? 'bg-success-500'
                    : 'bg-gray-200 dark:bg-gray-700'
                  }
                `} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      
      {/* Mobile Stepper (compact) */}
      <div className="sm:hidden">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Schritt {currentStep} von {steps.length}
          </span>
          <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
            {steps.find(s => s.id === currentStep)?.label}
          </span>
        </div>
        <div className="flex gap-1">
          {steps.map((step) => (
            <div
              key={step.id}
              className={`
                flex-1 h-2 rounded-full theme-transition
                ${currentStep >= step.id
                  ? currentStep === step.id
                    ? 'bg-primary-500'
                    : 'bg-success-500'
                  : 'bg-gray-200 dark:bg-gray-700'
                }
              `}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default WizardStepper;
